import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Modal } from "../../components/Modal";
import { SuspendOrgModal } from "../../components/SuspendOrgModal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Dropdown,
  Field,
  LoadingBlock,
  MENU_ITEM,
  Mono,
  PageHeader,
  Tag,
  Textarea,
} from "../../components/ui";
import { useCanWrite } from "../../auth/AuthProvider";
import { formatDateTime, formatRelative } from "../../lib/format";
import { useDecideRiskCase, useNoteRiskCase, useRiskCase, useRiskRules } from "../../lib/queries";
import { AREA_LABEL, PRIORITY_LABEL, PRIORITY_TONE, SEVERITY_LABEL, SEVERITY_TONE, SNOOZES, kindArea, subjectPath } from "../../lib/risk";
import type { RiskCaseActivity, RiskCaseDetail, RiskRule, RiskSignal } from "../../lib/types";

/**
 * One case, read top to bottom: what fired, grouped by area with the
 * rule's own guidance beside it, and the three things a reviewer can do.
 * Suspending goes through the organization's own modal -- same reason,
 * same mail to the customer -- and only then is the case marked.
 */
export function CaseDetailPage() {
  const { caseId = "" } = useParams();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const { data, isPending, error } = useRiskCase(caseId);
  const rules = useRiskRules();
  const decide = useDecideRiskCase(caseId);
  const [clearing, setClearing] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [note, setNote] = useState("");

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const open = data.status === "open" || data.status === "reviewing";
  const snoozed = Boolean(data.snoozedUntil && new Date(data.snoozedUntil) > new Date());
  const groups = groupSignals(data.signals);
  /* After a decision, the next file in queue order -- or the queue when
     there is none. Working down the list without going back to it. */
  const onward = () => void navigate(data.nextCaseId ? `/safety/${data.nextCaseId}` : "/safety");

  return (
    <>
      <PageHeader
        title={data.organization.name}
        description={describeCase(data)}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {canWrite && open && (
              <>
                {data.status === "open" ? (
                  <Button variant="secondary" loading={decide.isPending && decide.variables?.status === "reviewing"} onClick={() => decide.mutate({ status: "reviewing" })}>
                    Claim
                  </Button>
                ) : (
                  <Dropdown
                    align="end"
                    trigger={<span>{data.assignedToMe ? "Yours" : `${data.assignedToName ?? "Claimed"}`} ▾</span>}
                    triggerClassName="rounded-md border border-line px-3 py-1.5 text-sm text-fg transition-colors duration-200 ease-smooth hover:bg-raised"
                  >
                    {!data.assignedToMe && (
                      <button type="button" className={MENU_ITEM} onClick={() => decide.mutate({ status: "reviewing" })}>
                        Take over
                      </button>
                    )}
                    <button type="button" className={MENU_ITEM} onClick={() => decide.mutate({ status: "open" })}>
                      Hand back
                    </button>
                  </Dropdown>
                )}
                <Dropdown
                  align="end"
                  trigger={<span>{snoozed ? "Snoozed" : "Snooze"} ▾</span>}
                  triggerClassName="rounded-md border border-line px-3 py-1.5 text-sm text-fg transition-colors duration-200 ease-smooth hover:bg-raised"
                >
                  {SNOOZES.map((option) => (
                    <button
                      key={option.hours}
                      type="button"
                      className={MENU_ITEM}
                      onClick={() => decide.mutate({ status: "open", snoozeHours: option.hours }, { onSuccess: onward })}
                    >
                      {option.label}
                    </button>
                  ))}
                  {snoozed && (
                    <button type="button" className={MENU_ITEM} onClick={() => decide.mutate({ status: "open" })}>
                      Wake now
                    </button>
                  )}
                </Dropdown>
                <Button variant="secondary" onClick={() => setClearing(true)}>
                  Clear
                </Button>
                <Button variant="danger" onClick={() => setSuspending(true)} disabled={Boolean(data.organization.suspendedAt)}>
                  {data.organization.suspendedAt ? "Already suspended" : "Suspend organization"}
                </Button>
              </>
            )}
            {data.nextCaseId && (
              <Link to={`/safety/${data.nextCaseId}`} className="text-sm text-accent hover:underline">
                Next case →
              </Link>
            )}
          </div>
        }
      />
      {decide.error && <Alert>{decide.error.message}</Alert>}
      {snoozed && open && (
        <Alert tone="info">Snoozed until {formatDateTime(data.snoozedUntil)}. Out of the queue until then; it stays open and its signals keep counting.</Alert>
      )}

      {!open && (
        <Alert tone={data.status === "suspended" ? "error" : "info"}>
          {data.status === "suspended" ? "Suspended" : "Cleared"} by {data.decidedByName ?? "an operator"}{" "}
          {formatRelative(data.decidedAt)}.{data.decisionNote ? ` “${data.decisionNote}”` : ""}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {groups.map(([area, signals]) => (
            <Card key={area}>
              <CardHeader title={AREA_LABEL[area] ?? area} description={`${signals.length} signal${signals.length === 1 ? "" : "s"}`} />
              <ul className="divide-y divide-line">
                {signals.map((signal) => (
                  <SignalRow key={signal.id} signal={signal} orgId={data.organization.id} rule={rules.data?.get(signal.kind)} />
                ))}
              </ul>
            </Card>
          ))}
          {groups.length === 0 && (
            <Card>
              <p className="p-5 text-sm text-fg-subtle">No signals on this case.</p>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <ScoreWorking data={data} ruleTitle={(kind) => rules.data?.get(kind)?.title ?? kind} />
          <Card>
            <CardHeader title="Organization" />
            <dl className="space-y-2 px-5 py-4 text-sm">
              <Row label="Name">
                <Link to={`/organizations/${data.organization.id}`} className="text-accent hover:underline">
                  {data.organization.name}
                </Link>
              </Row>
              <Row label="Slug">
                <Mono>{data.organization.slug}</Mono>
              </Row>
              <Row label="Standing">{data.organization.suspendedAt ? `Suspended ${formatRelative(data.organization.suspendedAt)}` : "Active"}</Row>
              <Row label="Case opened">{formatRelative(data.openedAt)}</Row>
              <Row label="Reviewer">{data.assignedToName ?? "Unclaimed"}</Row>
            </dl>
          </Card>
          <ActivityCard caseId={caseId} activity={data.activity} open={open} />
          {data.history.length > 0 && (
            <Card>
              <CardHeader title="Earlier cases" description="A second file on the same tenant reads differently from a first." />
              <ul className="divide-y divide-line">
                {data.history.map((h) => (
                  <li key={h.id} className="px-5 py-3 text-sm">
                    <Link to={`/safety/${h.id}`} className="text-accent hover:underline">
                      {h.status} · score {h.score}
                    </Link>
                    <p className="text-xs text-fg-subtle">
                      {formatDateTime(h.openedAt)}
                      {h.decisionNote ? ` — “${h.decisionNote}”` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      <Modal open={clearing} onClose={() => setClearing(false)} title="Clear this case" description="Closes the file. The next signal on this organization opens a new one." dismissable={!decide.isPending}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            decide.mutate(
              { status: "cleared", note: note.trim() || undefined },
              {
                onSuccess: () => {
                  setClearing(false);
                  onward();
                },
              },
            );
          }}
          className="space-y-4"
        >
          <Field label="Why" htmlFor="clear-note" hint="For the next reviewer. Kept on the case and in the audit log.">
            <Textarea id="clear-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A storefront called “terminal”; the CPU was a build." required />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setClearing(false)} disabled={decide.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={decide.isPending}>
              {data.nextCaseId ? "Clear and go to next" : "Clear case"}
            </Button>
          </div>
        </form>
      </Modal>

      <SuspendOrgModal
        orgId={data.organization.id}
        name={data.organization.name}
        open={suspending}
        onClose={() => setSuspending(false)}
        onSuspended={(reason) =>
          decide.mutate({ status: "suspended", note: reason ?? "Suspended from the case." }, { onSuccess: onward })
        }
      />
    </>
  );
}

function describeCase(data: RiskCaseDetail): string {
  const critical = data.counts.critical > 0 ? `, ${data.counts.critical} critical` : "";
  return `${PRIORITY_LABEL[data.priority]} priority · score ${data.score} · ${data.counts.signals} signal${data.counts.signals === 1 ? "" : "s"}${critical} · latest ${formatRelative(data.lastSignalAt)}`;
}

/**
 * The score's working, so a reviewer can see why this file is where it is
 * in the queue rather than trusting a number: each rule's contribution
 * after the cap, and the three multipliers with what they mean.
 */
function ScoreWorking({ data, ruleTitle }: { data: RiskCaseDetail; ruleTitle: (kind: string) => string }) {
  const d = data.scoreDetail;
  const kinds = Object.entries(d.kinds ?? {}).sort((a, b) => b[1].contribution - a[1].contribution);
  const multipliers: { label: string; value: number; why: string }[] = [
    { label: "Corroboration", value: d.multipliers?.corroboration ?? 1, why: `${(d.areas ?? []).length} area${(d.areas ?? []).length === 1 ? "" : "s"} agree` },
    { label: "History", value: d.multipliers?.history ?? 1, why: (d.multipliers?.history ?? 1) > 1 ? "suspended from an earlier case" : "no earlier suspension" },
    { label: "Age", value: d.multipliers?.youth ?? 1, why: (d.multipliers?.youth ?? 1) > 1 ? "organization under a week old" : "established organization" },
  ];
  return (
    <Card>
      <CardHeader
        title="Why this score"
        description={`${PRIORITY_LABEL[data.priority]} priority. Base ${d.base ?? 0}, then the multipliers.`}
        action={<Tag tone={PRIORITY_TONE[data.priority]}>{PRIORITY_LABEL[data.priority]}</Tag>}
      />
      <dl className="space-y-1.5 px-5 py-4 text-sm">
        {kinds.map(([kind, entry]) => (
          <div key={kind} className="flex items-baseline justify-between gap-3">
            <dt className="text-fg-subtle">
              {ruleTitle(kind)}
              <span className="text-xs"> · {entry.occurrences} time{entry.occurrences === 1 ? "" : "s"}{entry.raw > entry.contribution ? ", capped" : ""}</span>
            </dt>
            <dd className="tabular-nums text-fg">{entry.contribution}</dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 border-t border-line pt-2">
          <dt className="text-fg-subtle">Base</dt>
          <dd className="tabular-nums text-fg">{d.base ?? 0}</dd>
        </div>
        {multipliers.map((m) => (
          <div key={m.label} className="flex items-baseline justify-between gap-3">
            <dt className="text-fg-subtle">
              {m.label}
              <span className="text-xs"> · {m.why}</span>
            </dt>
            <dd className="tabular-nums text-fg">×{m.value}</dd>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 border-t border-line pt-2 font-medium">
          <dt className="text-fg">Score</dt>
          <dd className="tabular-nums text-fg">{data.score}</dd>
        </div>
      </dl>
    </Card>
  );
}

function groupSignals(signals: RiskSignal[]): [string, RiskSignal[]][] {
  const groups = new Map<string, RiskSignal[]>();
  for (const signal of signals) {
    const area = kindArea(signal.kind);
    groups.set(area, [...(groups.get(area) ?? []), signal]);
  }
  // Areas with a critical signal first, then by how much is in them.
  const weight = (list: RiskSignal[]) => (list.some((s) => s.severity === "critical") ? 1000 : 0) + list.length;
  return [...groups].sort((a, b) => weight(b[1]) - weight(a[1]));
}

function SignalRow({ signal, orgId, rule }: { signal: RiskSignal; orgId: string; rule: RiskRule | undefined }) {
  const href = subjectPath(signal.subject, orgId);
  return (
    <li className="space-y-1 px-5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={SEVERITY_TONE[signal.severity]}>{SEVERITY_LABEL[signal.severity]}</Tag>
        <span className="text-sm font-medium text-fg">{rule?.title ?? signal.kind}</span>
        {/* "Tried once" and "tried nine times" are different facts; the row
            counts its repeats rather than the case growing nine rows. */}
        {signal.occurrences > 1 && <Tag tone="neutral">×{signal.occurrences}</Tag>}
        <span className="ml-auto text-xs text-fg-subtle" title={`First ${formatDateTime(signal.createdAt)}, last ${formatDateTime(signal.lastSeenAt)}`}>
          {formatRelative(signal.lastSeenAt)}
        </span>
      </div>
      <p className="text-sm text-fg">
        {href ? (
          <Link to={href} className="hover:underline">
            {signal.summary}
          </Link>
        ) : (
          signal.summary
        )}
      </p>
      {/* The rule's own guidance, beside the thing it fired on: what to look
          at to decide, written once in the registry rather than remembered. */}
      {rule && <p className="text-xs leading-5 text-fg-subtle">{rule.guidance}</p>}
    </li>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="text-right text-fg">{children}</dd>
    </div>
  );
}

const ACTIVITY_LABEL: Record<string, string> = {
  "risk_case.reviewing": "claimed the case",
  "risk_case.open": "handed it back",
  "risk_case.snoozed": "snoozed it",
  "risk_case.noted": "noted",
  "risk_case.cleared": "cleared the case",
  "risk_case.suspended": "suspended the organization",
};

/**
 * What people did with the file, in order, and a way to add to it without
 * deciding anything. A note here is for the next reviewer -- "asked the
 * customer, waiting" -- and it is the same audit row the rest of the
 * console reads, so nothing about the case lives only in someone's head.
 */
function ActivityCard({ caseId, activity, open }: { caseId: string; activity: RiskCaseActivity[]; open: boolean }) {
  const add = useNoteRiskCase(caseId);
  const [draft, setDraft] = useState("");
  function submit(event: FormEvent) {
    event.preventDefault();
    const note = draft.trim();
    if (!note) return;
    add.mutate(note, { onSuccess: () => setDraft("") });
  }
  return (
    <Card>
      <CardHeader title="Activity" description={activity.length === 0 ? "Nobody has touched this case yet." : undefined} />
      {activity.length > 0 && (
        <ol className="divide-y divide-line">
          {activity.map((entry) => (
            <li key={entry.id} className="px-5 py-2.5 text-sm">
              <p>
                <span className="font-medium text-fg">{entry.actorName ?? "Someone"}</span>{" "}
                <span className="text-fg-subtle">
                  {ACTIVITY_LABEL[entry.action] ?? entry.action}
                  {entry.hours ? ` for ${entry.hours} hours` : ""}
                </span>
                <span className="float-right text-xs text-fg-subtle" title={formatDateTime(entry.createdAt)}>
                  {formatRelative(entry.createdAt)}
                </span>
              </p>
              {entry.note && <p className="mt-0.5 text-fg">“{entry.note}”</p>}
            </li>
          ))}
        </ol>
      )}
      {open && (
        <form onSubmit={submit} className="space-y-2 border-t border-line px-5 py-3">
          {add.error && <Alert>{add.error.message}</Alert>}
          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="A note for the next reviewer…"
            aria-label="Add a note"
            maxLength={2000}
          />
          <div className="flex justify-end">
            <Button type="submit" variant="secondary" size="sm" loading={add.isPending} disabled={draft.trim().length === 0}>
              Add note
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
